import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Center,
  Group,
  Loader,
  Modal,
  Pagination,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconDownload, IconMicrophone, IconPlayerPlay, IconWaveSine } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { useOrg } from '@/hooks/use-org';
import { durationBetween, formatDateTime, formatDuration, formatPhone } from '../-components/format';
import { StatTile } from '../-components/stat-cards';
import { CallRecordingsCountSubscription, CallRecordingsSubscription } from './-components/graphql';

const PAGE_SIZE = 50;

type RecordingRow = {
  id: string;
  flowName: string | null;
  format: string | null;
  channels: string | null;
  startedAt: string;
};

export const Route = createFileRoute('/_authed/settings/recordings')({
  component: SettingsRecordingsPage,
});

function downloadName(startedAt: string, format: string) {
  return `recording-${new Date(startedAt).toISOString().replaceAll(':', '-')}.${format.toLowerCase()}`;
}

function PlaybackModal({
  recording,
  playbackUrl,
  playbackError,
  isLoading,
  onClose,
}: {
  recording: RecordingRow | null;
  playbackUrl: string | null;
  playbackError: string | null;
  isLoading: boolean;
  onClose: () => void;
}) {
  return (
    <Modal
      opened={recording !== null}
      onClose={onClose}
      title={recording ? `Recording from ${formatDateTime(recording.startedAt)}` : 'Recording'}
      size="lg"
    >
      <Stack gap="md">
        {recording && (
          <Group gap="xs">
            <Badge variant="light" color="indigo">
              {recording.flowName ?? 'Unknown flow'}
            </Badge>
            {recording.format && (
              <Badge variant="light" color="gray">
                {recording.format}
              </Badge>
            )}
            {recording.channels && (
              <Badge variant="dot" color="gray">
                {recording.channels}
              </Badge>
            )}
          </Group>
        )}

        {isLoading && (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        )}

        <ErrorAlert message={playbackError} />

        {playbackUrl && <audio key={playbackUrl} src={playbackUrl} controls autoPlay style={{ width: '100%' }} />}
      </Stack>
    </Modal>
  );
}

function Recordings({ organizationId }: { organizationId: string }) {
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<RecordingRow | null>(null);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const [{ data }] = useSubscription({
    query: CallRecordingsSubscription,
    variables: { organizationId, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE },
  });
  const [{ data: countData }] = useSubscription({
    query: CallRecordingsCountSubscription,
    variables: { organizationId },
  });

  useEffect(() => {
    let cancelled = false;

    void api['phone-numbers']['sync-inbound-webhook'].post({ organizationId }).then(({ error }) => {
      if (error && !cancelled) setPageError(getErrorMessage(error, 'Unable to verify Telnyx webhook configuration'));
    });

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  if (!data || !countData) {
    return (
      <Center h="30vh">
        <Loader size="lg" />
      </Center>
    );
  }

  const total = countData.callRecordingsAggregate.aggregate?.count ?? 0;
  const totalDurationSeconds = data.callRecordings.reduce(
    (sum, recording) => sum + durationBetween(recording.startedAt, recording.endedAt),
    0,
  );

  const openPlayer = async (recording: RecordingRow) => {
    setSelected(recording);
    setPlaybackUrl(null);
    setPlaybackError(null);
    setPageError(null);
    setResolvingId(recording.id);

    const { data: link, error } = await api.recordings({ id: recording.id })['access-link'].get();

    setResolvingId(null);

    if (error) return setPlaybackError(getErrorMessage(error, 'Unable to load recording'));

    setPlaybackUrl(link.url);
  };

  const download = async (recording: RecordingRow) => {
    setPageError(null);
    setDownloadingId(recording.id);

    const { data: link, error } = await api.recordings({ id: recording.id })['access-link'].get();

    setDownloadingId(null);

    if (error) return setPageError(getErrorMessage(error, 'Unable to load recording'));

    const anchor = document.createElement('a');

    anchor.href = link.url;
    anchor.target = '_blank';
    anchor.rel = 'noreferrer';
    anchor.download = downloadName(recording.startedAt, link.format);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  return (
    <>
      <Stack gap="xl">
        <div>
          <Title order={2}>Recordings</Title>
          <Text size="sm" c="dimmed" mt="2xs">
            Review recorded calls, listen in-browser, and download the original file.
          </Text>
        </div>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <StatTile label="Total Recordings" value={total} icon={<IconMicrophone size={24} />} color="indigo" />
          <StatTile
            label="Recorded Time (page)"
            value={formatDuration(totalDurationSeconds)}
            icon={<IconWaveSine size={24} />}
            color="cyan"
          />
        </SimpleGrid>

        <ErrorAlert message={pageError} />

        {data.callRecordings.length === 0 ? (
          <Paper p="3xl" radius="lg" withBorder>
            <Stack align="center" gap="md">
              <ThemeIcon size={52} radius="xl" variant="light" color="gray">
                <IconMicrophone size={26} />
              </ThemeIcon>
              <div style={{ textAlign: 'center' }}>
                <Text fw={600}>No recordings yet</Text>
                <Text size="sm" c="dimmed" mt="xs">
                  Recorded calls will appear here once Telnyx finishes saving them.
                </Text>
              </div>
            </Stack>
          </Paper>
        ) : (
          <Paper p={0} radius="lg" style={{ overflow: 'hidden' }} withBorder>
            <Table.ScrollContainer minWidth={860}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Recorded</Table.Th>
                    <Table.Th>Caller</Table.Th>
                    <Table.Th>Called</Table.Th>
                    <Table.Th>Flow</Table.Th>
                    <Table.Th>Length</Table.Th>
                    <Table.Th>Format</Table.Th>
                    <Table.Th w={92} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.callRecordings.map((recording) => {
                    const row: RecordingRow = {
                      id: recording.id,
                      flowName: recording.flow?.name ?? null,
                      format: recording.format,
                      channels: recording.channels,
                      startedAt: recording.startedAt,
                    };

                    return (
                      <Table.Tr key={recording.id}>
                        <Table.Td>
                          <Stack gap={2}>
                            <Text size="sm" fw={500}>
                              {formatDateTime(recording.startedAt)}
                            </Text>
                            {recording.direction && (
                              <Badge
                                size="xs"
                                variant="light"
                                color={recording.direction === 'inbound' ? 'green' : 'blue'}
                              >
                                {recording.direction}
                              </Badge>
                            )}
                          </Stack>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{formatPhone(recording.fromNumber ?? '')}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{formatPhone(recording.toNumber ?? '')}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c={recording.flow ? undefined : 'dimmed'}>
                            {recording.flow?.name ?? 'Unknown'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {formatDuration(durationBetween(recording.startedAt, recording.endedAt))}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            {recording.format && (
                              <Badge size="sm" variant="light" color="gray">
                                {recording.format}
                              </Badge>
                            )}
                            {recording.channels && (
                              <Badge size="sm" variant="dot" color="gray">
                                {recording.channels}
                              </Badge>
                            )}
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={6} justify="flex-end">
                            <Tooltip label="Listen">
                              <ActionIcon
                                variant="light"
                                color="indigo"
                                onClick={() => void openPlayer(row)}
                                disabled={resolvingId === recording.id || downloadingId === recording.id}
                              >
                                {resolvingId === recording.id ? <Loader size={14} /> : <IconPlayerPlay size={14} />}
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Download">
                              <ActionIcon
                                variant="light"
                                color="gray"
                                onClick={() => void download(row)}
                                disabled={resolvingId === recording.id || downloadingId === recording.id}
                              >
                                {downloadingId === recording.id ? <Loader size={14} /> : <IconDownload size={14} />}
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            {total > PAGE_SIZE && (
              <Group justify="center" p="md">
                <Pagination value={page} onChange={setPage} total={Math.ceil(total / PAGE_SIZE)} size="sm" />
              </Group>
            )}
          </Paper>
        )}
      </Stack>

      <PlaybackModal
        recording={selected}
        playbackUrl={playbackUrl}
        playbackError={playbackError}
        isLoading={resolvingId !== null && selected !== null}
        onClose={() => {
          setSelected(null);
          setPlaybackUrl(null);
          setPlaybackError(null);
          setResolvingId(null);
        }}
      />
    </>
  );
}

function SettingsRecordingsPage() {
  const { organizationId } = useOrg();

  if (!organizationId) {
    return (
      <Center h="30vh">
        <Loader size="lg" />
      </Center>
    );
  }

  return <Recordings organizationId={organizationId} />;
}
